import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, DatePicker, Button, Card, message, Empty, Tag, Tooltip, Space, Modal, Input, Checkbox, Upload, Progress } from 'antd';
import { CalendarOutlined, UserOutlined, ArrowLeftOutlined, DownloadOutlined, EditOutlined, PaperClipOutlined, EyeOutlined, FileOutlined, FileWordOutlined, FileExcelOutlined, FilePptOutlined, FilePdfOutlined, PictureOutlined, CommentOutlined, DeleteOutlined, InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import AnnotationPanel from './AnnotationPanel';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const { Option } = Select;
const { RangePicker } = DatePicker;

// 格式化文件大小
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// 根据 mime_type 或文件名后缀获取文件图标
const getFileIcon = (mimeType, filename) => {
  const name = (filename || '').toLowerCase();
  const mime = (mimeType || '').toLowerCase();
  if (mime.startsWith('image/') || /\.(png|jpg|jpeg|gif|bmp|webp|svg)$/.test(name)) return <PictureOutlined style={{ color: '#52c41a' }} />;
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return <FilePdfOutlined style={{ color: '#f5222d' }} />;
  if (mime.includes('word') || mime.includes('document') || name.endsWith('.doc') || name.endsWith('.docx')) return <FileWordOutlined style={{ color: '#2f54eb' }} />;
  if (mime.includes('sheet') || mime.includes('excel') || name.endsWith('.xls') || name.endsWith('.xlsx')) return <FileExcelOutlined style={{ color: '#52c41a' }} />;
  if (mime.includes('presentation') || mime.includes('powerpoint') || name.endsWith('.ppt') || name.endsWith('.pptx')) return <FilePptOutlined style={{ color: '#fa8c16' }} />;
  return <FileOutlined style={{ color: '#8c8c8c' }} />;
};

// 获取预览 URL
const getPreviewUrl = (url, mimeType, filename) => {
  const mime = (mimeType || '').toLowerCase();
  const name = (filename || '').toLowerCase();
  // PDF → 直接用 iframe
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return url;
  // 图片 → 直接 img
  if (mime.startsWith('image/') || /\.(png|jpg|jpeg|gif|bmp|webp|svg)$/.test(name)) return url;
  // Office 文档 → 微软 Office Online Viewer
  if (
    mime.includes('word') || mime.includes('document') || name.endsWith('.doc') || name.endsWith('.docx') ||
    mime.includes('sheet') || mime.includes('excel') || name.endsWith('.xls') || name.endsWith('.xlsx') ||
    mime.includes('presentation') || mime.includes('powerpoint') || name.endsWith('.ppt') || name.endsWith('.pptx')
  ) {
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
  }
  return null;
};

// 打开预览窗口
const openPreview = (url, mimeType, filename) => {
  const previewUrl = getPreviewUrl(url, mimeType, filename);
  if (previewUrl) {
    window.open(previewUrl, '_blank');
  } else {
    message.info('该文件类型暂不支持在线预览');
  }
};

export default function WorkLogOverview() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]); // 多选项目组
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]); // 日期范围
  const [selectedMembers, setSelectedMembers] = useState([]); // 多选成员
  const [logs, setLogs] = useState({});
  const [loading, setLoading] = useState(false);
  const [allMembers, setAllMembers] = useState([]); // 所有成员列表
  
  // 编辑弹窗状态
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editForm, setEditForm] = useState({ focus: '', detail: '', remarks: '', date: '' });
  const [editSaving, setEditSaving] = useState(false);
  
  // 编辑弹窗附件相关状态
  const [editAttachments, setEditAttachments] = useState([]);
  const [editPendingFiles, setEditPendingFiles] = useState([]);
  const [editDeletingAtt, setEditDeletingAtt] = useState(null);
  const [editUploadProgress, setEditUploadProgress] = useState(null);
  const editFileInputRef = useRef(null);
  const [editDragOver, setEditDragOver] = useState(false);
  
  // 批注弹窗状态
  const [annotationLogId, setAnnotationLogId] = useState(null);
  const currentUser = localStorage.getItem('notes_user') || '';
  const [userMap, setUserMap] = useState({}); // username -> realname 映射

  useEffect(() => {
    loadGroups();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/auth/users`, {
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      const data = await resp.json();
      if (data.success && data.users) {
        const map = {};
        data.users.forEach(u => { map[u.username] = u.realname || u.username; });
        setUserMap(map);
      }
    } catch (e) {
      console.error('加载用户列表失败', e);
    }
  };

  useEffect(() => {
    if (selectedGroups.length > 0) {
      loadDailySummary();
      // 保存到 localStorage
      localStorage.setItem('worklog_selected_groups', JSON.stringify(selectedGroups));
    }
  }, [selectedGroups, dateRange, selectedMembers]);

  // 当项目组变化时，更新成员列表
  useEffect(() => {
    if (selectedGroups.includes('all')) {
      // 选择了全部，选中所有项目组
      setSelectedGroups(groups.map(g => g.id));
    } else if (selectedGroups.length > 0) {
      const members = new Set();
      selectedGroups.forEach(gid => {
        const group = groups.find(g => g.id === gid);
        if (group && group.members) {
          group.members.forEach(m => members.add(m));
        }
      });
      setAllMembers(Array.from(members));
      setSelectedMembers([]); // 重置成员选择
    }
  }, [selectedGroups, groups]);

  // 当成员变化时，处理"全部"选项
  useEffect(() => {
    if (selectedMembers.includes('all')) {
      setSelectedMembers(allMembers);
    }
  }, [selectedMembers, allMembers]);

  const loadGroups = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/work-groups`, {
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      const data = await resp.json();
      if (Array.isArray(data) && data.length > 0) {
        setGroups(data);
        // 从 localStorage 读取上次选择的项目组
        const savedGroups = localStorage.getItem('worklog_selected_groups');
        if (savedGroups) {
          try {
            const parsed = JSON.parse(savedGroups);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setSelectedGroups(parsed);
              return;
            }
          } catch (e) {}
        }
        setSelectedGroups([data[0].id]); // 默认选中第一个
      }
    } catch (e) {
      message.error('加载项目组失败');
    }
  };

  const loadDailySummary = async () => {
    setLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const groupIds = selectedGroups.join(',');
      const memberIds = selectedMembers.join(',');
      const resp = await fetch(
        `${API_URL}/api/work-logs/daily-summary?groupIds=${groupIds}&startDate=${startDate}&endDate=${endDate}&memberIds=${memberIds}`,
        {
          headers: { 'x-token': localStorage.getItem('notes_token') }
        }
      );
      const data = await resp.json();
      setLogs(data);
    } catch (e) {
      message.error('加载日志失败');
    }
    setLoading(false);
  };

  // 快捷日期：本周（周一到周日）
  const setThisWeek = () => {
    const startOfWeek = dayjs().startOf('isoWeek'); // 周一
    const endOfWeek = dayjs().endOf('isoWeek'); // 周日
    setDateRange([startOfWeek, endOfWeek]);
  };

  // 快捷日期：今天
  const setToday = () => {
    setDateRange([dayjs(), dayjs()]);
  };

  // 快捷日期：昨天
  const setYesterday = () => {
    const yesterday = dayjs().subtract(1, 'day');
    setDateRange([yesterday, yesterday]);
  };

  const group = groups.find(g => g.id === selectedGroups[0]);

  // 打开编辑弹窗
  const handleEditClick = async (log) => {
    setEditingLog(log);
    setEditForm({
      focus: log.focus || '',
      detail: log.detail || '',
      remarks: log.remarks || '',
      date: log.date || ''
    });
    // 加载附件列表
    setEditAttachments(log.attachments || []);
    setEditPendingFiles([]);
    setEditModalVisible(true);
  };

  // 删除附件
  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('确定删除此附件？')) return;
    setEditDeletingAtt(attId);
    try {
      const resp = await fetch(`${API_URL}/api/work-log-attachments/${attId}`, {
        method: 'DELETE',
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      if (resp.ok) {
        setEditAttachments(prev => prev.filter(a => a.id !== attId));
        message.success('附件已删除');
        // 刷新日志列表数据
        loadDailySummary();
      } else {
        message.error('删除失败');
      }
    } catch (e) {
      message.error('删除失败');
    }
    setEditDeletingAtt(null);
  };

  // 上传新附件
  const handleUploadEditAttachments = async () => {
    if (editPendingFiles.length === 0) return;
    if (!editingLog) return;

    const total = editPendingFiles.length;
    const allNewAtts = [];
    for (let i = 0; i < editPendingFiles.length; i++) {
      const file = editPendingFiles[i];
      setEditUploadProgress({
        current: i + 1,
        total,
        percent: 0,
        fileName: file.name
      });

      const formData = new FormData();
      formData.append('files', file);

      try {
        const resp = await fetch(`${API_URL}/api/work-logs/${editingLog.id}/attachments`, {
          method: 'POST',
          headers: { 'x-token': localStorage.getItem('notes_token') },
          body: formData
        });
        if (resp.ok) {
          const newAtts = await resp.json();
          allNewAtts.push(...newAtts);
          setEditAttachments(prev => [...prev, ...newAtts]);
        } else {
          message.error(`上传失败: ${file.name}`);
        }
      } catch (e) {
        message.error(`上传失败: ${file.name}`);
      }
    }
    setEditUploadProgress(null);
    setEditPendingFiles([]);
    message.success('附件上传完成');
    // 更新 editingLog 的 attachments，确保再次打开弹窗时能看到
    if (allNewAtts.length > 0) {
      setEditingLog(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...allNewAtts]
      }));
    }
    // 刷新日志列表数据，使附件在卡片中显示
    loadDailySummary();
  };

  // 处理文件选择
  const handleEditFilesSelect = (files) => {
    const fileArray = Array.from(files);
    if (editPendingFiles.length + fileArray.length > 10) {
      message.warning('最多选择10个附件');
      return;
    }
    setEditPendingFiles(prev => [...prev, ...fileArray]);
  };

  // 移除待上传文件
  const handleRemoveEditPendingFile = (index) => {
    setEditPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // 拖拽处理
  const handleEditDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleEditFilesSelect(e.dataTransfer.files);
    }
  };

  const handleEditDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditDragOver(true);
  };

  const handleEditDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditDragOver(false);
  };

  // 保存编辑
  const handleEditSave = async () => {
    if (!editingLog) return;
    setEditSaving(true);
    try {
      // 先上传待上传的附件
      if (editPendingFiles.length > 0) {
        const total = editPendingFiles.length;
        const allNewAtts = [];
        for (let i = 0; i < editPendingFiles.length; i++) {
          const file = editPendingFiles[i];
          setEditUploadProgress({
            current: i + 1,
            total,
            percent: 0,
            fileName: file.name
          });

          const formData = new FormData();
          formData.append('files', file);

          try {
            const resp = await fetch(`${API_URL}/api/work-logs/${editingLog.id}/attachments`, {
              method: 'POST',
              headers: { 'x-token': localStorage.getItem('notes_token') },
              body: formData
            });
            if (resp.ok) {
              const newAtts = await resp.json();
              allNewAtts.push(...newAtts);
            } else {
              message.error(`上传失败: ${file.name}`);
            }
          } catch (e) {
            message.error(`上传失败: ${file.name}`);
          }
        }
        setEditUploadProgress(null);
        setEditPendingFiles([]);
        if (allNewAtts.length > 0) {
          message.success(`已上传 ${allNewAtts.length} 个附件`);
        }
      }

      // 保存日志内容
      const resp = await fetch(`${API_URL}/api/work-logs/${editingLog.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-token': localStorage.getItem('notes_token')
        },
        body: JSON.stringify({
          focus: editForm.focus,
          detail: editForm.detail,
          date: editForm.date,
          remarks: editForm.remarks
        })
      });
      if (resp.ok) {
        message.success('修改成功');
        setEditModalVisible(false);
        loadDailySummary(); // 刷新数据
      } else {
        const err = await resp.json();
        message.error(err.error || '修改失败');
      }
    } catch (e) {
      message.error('修改失败');
    }
    setEditSaving(false);
  };

  const exportToExcel = async () => {
    // 收集所有日志数据
    const allLogs = [];
    Object.entries(logs).forEach(([userId, userLogs]) => {
      userLogs.forEach(log => {
        allLogs.push({
          date: log.date,
          project: log.project_name || log.project_id || '',
          userName: log.user_name || userId,
          focus: log.focus || '',
          detail: log.detail || '',
          remarks: log.remarks || ''
        });
      });
    });

    // 按日期、项目、姓名排序
    allLogs.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      if (a.project !== b.project) return a.project.localeCompare(b.project);
      return a.userName.localeCompare(b.userName);
    });

    // 创建工作簿和工作表
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('日志总览');

    // 设置列
    worksheet.columns = [
      { header: '日期', key: 'date', width: 12 },
      { header: '项目', key: 'project', width: 20 },
      { header: '姓名', key: 'userName', width: 12 },
      { header: '工作重点', key: 'focus', width: 40 },
      { header: '详细工作内容', key: 'detail', width: 60 },
      { header: '其他说明', key: 'remarks', width: 40 }
    ];

    // 添加数据
    allLogs.forEach(log => {
      worksheet.addRow(log);
    });

    // 设置表头样式
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE8E8E8' }
    };

    // 设置所有数据行的样式
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.alignment = { vertical: 'middle', wrapText: true };
      }
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    // 合并日期和项目列相同值的单元格
    // 先合并日期列
    let dateStart = 2;
    for (let i = 2; i <= allLogs.length + 1; i++) {
      const curr = allLogs[i - 1];
      const prev = allLogs[i - 2];

      if (i === allLogs.length + 1 || (curr && prev && curr.date !== prev.date)) {
        if (i > dateStart) {
          worksheet.mergeCells(dateStart, 1, i - 1, 1);
        }
        dateStart = i;
      }
    }

    // 再合并项目列（在相同日期内合并相同项目）
    let projectStart = 2;
    for (let i = 2; i <= allLogs.length + 1; i++) {
      const curr = allLogs[i - 1];
      const prev = allLogs[i - 2];

      if (i === allLogs.length + 1 || (curr && prev && (curr.date !== prev.date || curr.project !== prev.project))) {
        if (i > projectStart) {
          worksheet.mergeCells(projectStart, 2, i - 1, 2);
        }
        projectStart = i;
      }
    }

    // 导出文件
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `日志总览_${selectedDate.format('YYYY-MM-DD')}_${group?.name || ''}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ position: 'sticky', top: 0, zIndex: 100, background: 'transparent', padding: '16px 0', marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/work-logs')} style={{ marginRight: 16 }}>
          返回
        </Button>
        <h2 style={{ margin: 0, marginRight: 24 }}><CalendarOutlined /> 日志总览</h2>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
          <div>
            <span style={{ marginRight: 8 }}>项目组：</span>
            <Select
              mode="multiple"
              value={selectedGroups}
              onChange={setSelectedGroups}
              style={{ minWidth: 200 }}
              placeholder="选择项目组"
              maxTagCount="responsive"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value="all" key="all">全部</Option>
              {groups.map(g => (
                <Option key={g.id} value={g.id}>{g.name}</Option>
              ))}
            </Select>
          </div>
          <div>
            <span style={{ marginRight: 8 }}>成员：</span>
            <Select
              mode="multiple"
              value={selectedMembers}
              onChange={setSelectedMembers}
              style={{ minWidth: 200 }}
              placeholder="选择成员"
              maxTagCount="responsive"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value="all" key="all">全部</Option>
              {allMembers.map(m => (
                <Option key={m} value={m}>{userMap[m] || m}</Option>
              ))}
            </Select>
          </div>
          <div>
            <span style={{ marginRight: 8 }}>日期：</span>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              style={{ width: 240 }}
            />
            <Button size="small" onClick={setToday} style={{ marginLeft: 8 }}>今天</Button>
            <Button size="small" onClick={setYesterday} style={{ marginLeft: 4 }}>昨天</Button>
            <Button size="small" onClick={setThisWeek} style={{ marginLeft: 4 }}>本周</Button>
          </div>
          <Button type="primary" onClick={loadDailySummary} loading={loading}>
            查询
          </Button>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={exportToExcel}
            disabled={Object.keys(logs).length === 0}
          >
            导出Excel
          </Button>
        </div>
      </div>

      {Object.keys(logs).length === 0 && !loading && (
        <Card>
          <Empty description="当日暂无日志" />
        </Card>
      )}

      {Object.entries(logs).map(([userId, userLogs]) => (
        <Card
          key={userId}
          title={
            <span>
              <UserOutlined style={{ marginRight: 8 }} />
              {userLogs[0]?.user_name || userId}
              <Tag color="blue" style={{ marginLeft: 12 }}>
                {userLogs.length} 条日志
              </Tag>
            </span>
          }
          style={{ marginBottom: 16 }}
        >
          {userLogs.map((log, idx) => (
            <div key={log.id || idx} style={{ marginBottom: 16, position: 'relative', padding: '12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
              {/* 日期标签 */}
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag color="green">{log.date}</Tag>
                {log.project_name && (
                  <span style={{ color: '#722ed1', fontSize: 13 }}>【{log.project_name}】</span>
                )}
              </div>
              {/* 批注图标 */}
              {log.id && (
                <div 
                  className={`log-annotation-badge ${log.annotation_count > 0 ? 'has-annotations' : ''}`}
                  onClick={() => setAnnotationLogId(log.id)}
                  title="查看/添加批注"
                >
                  <span className="badge-icon">💬</span>
                  {log.annotation_count > 0 && (
                    <span className="badge-count">{log.annotation_count}</span>
                  )}
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: '#1890ff' }}>【工作重点】</strong>
                <div style={{ marginTop: 4, color: '#333' }}>{log.focus}</div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: '#1890ff' }}>【详细内容】</strong>
                <div style={{ marginTop: 4, color: '#666', whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {log.detail}
                </div>
              </div>
              {log.remarks && (
                <div style={{ marginBottom: 8 }}>
                  <strong style={{ color: '#fa8c16' }}>【其他说明】</strong>
                  <div style={{ marginTop: 4, color: '#666', whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                    {log.remarks}
                  </div>
                </div>
              )}
              {log.attachments && log.attachments.length > 0 && (
                <div style={{ marginTop: 8, marginBottom: 8, padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                  <div style={{ marginBottom: 6, color: '#8c8c8c', fontSize: 13 }}>
                    <PaperClipOutlined style={{ marginRight: 4 }} />
                    附件（{log.attachments.length}）
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {log.attachments.map((att) => (
                      <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>{getFileIcon(att.mime_type, att.filename)}</span>
                        <a
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ flex: 1, color: '#1890ff', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}
                          title={att.filename}
                        >
                          {att.filename}
                        </a>
                        {att.size && (
                          <span style={{ color: '#bfbfbf', fontSize: 12, whiteSpace: 'nowrap' }}>
                            {formatFileSize(att.size)}
                          </span>
                        )}
                        <Tooltip title="预览">
                          <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => openPreview(att.url, att.mime_type, att.filename)}
                            style={{ color: '#1890ff' }}
                          />
                        </Tooltip>
                        <Tooltip title="下载">
                          <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined />}
                            href={att.url}
                            target="_blank"
                            style={{ color: '#52c41a' }}
                          />
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {idx < userLogs.length - 1 && <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px dashed #e8e8e8' }} />}
              {/* 编辑按钮 - 仅本人可见 */}
              {log.user_id === (localStorage.getItem('notes_user') || '') && (
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditClick(log)}
                  >
                    修改
                  </Button>
                </div>
              )}
            </div>
          ))}
        </Card>
      ))}

      {/* 编辑弹窗 */}
      <Modal
        title="修改日志"
        open={editModalVisible}
        onOk={handleEditSave}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={editSaving}
        width={650}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>日期</label>
          <Input
            value={editForm.date}
            onChange={e => setEditForm({ ...editForm, date: e.target.value })}
            placeholder="YYYY-MM-DD"
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>工作重点</label>
          <Input
            value={editForm.focus}
            onChange={e => setEditForm({ ...editForm, focus: e.target.value })}
            placeholder="请输入工作重点"
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>详细工作内容</label>
          <Input.TextArea
            value={editForm.detail}
            onChange={e => setEditForm({ ...editForm, detail: e.target.value })}
            rows={6}
            placeholder="请输入详细工作内容"
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>其他说明</label>
          <Input.TextArea
            value={editForm.remarks}
            onChange={e => setEditForm({ ...editForm, remarks: e.target.value })}
            rows={3}
            placeholder="请输入其他说明、问题、备注或重要事项"
          />
        </div>

        {/* 附件管理区域 */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            📎 附件 ({editAttachments.length})
          </label>

          {/* 已有附件列表 */}
          {editAttachments.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {editAttachments.map(att => (
                <div key={att.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  marginBottom: 6
                }}>
                  {getFileIcon(att.mime_type, att.filename)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {att.filename}
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>
                      {formatFileSize(att.size)}
                    </div>
                  </div>
                  <a
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1890ff', fontSize: 12 }}
                  >
                    下载
                  </a>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={editDeletingAtt === att.id ? <LoadingOutlined /> : <DeleteOutlined />}
                    onClick={() => handleDeleteAttachment(att.id)}
                    disabled={editDeletingAtt === att.id}
                    title="删除附件"
                  />
                </div>
              ))}
            </div>
          )}

          {/* 拖拽/点击上传区域 */}
          <div
            onDrop={handleEditDrop}
            onDragOver={handleEditDragOver}
            onDragLeave={handleEditDragLeave}
            onClick={() => editFileInputRef.current?.click()}
            style={{
              border: `2px dashed ${editDragOver ? '#1890ff' : '#d9d9d9'}`,
              borderRadius: 8,
              padding: '16px 12px',
              textAlign: 'center',
              cursor: 'pointer',
              background: editDragOver ? '#e6f7ff' : '#fafafa',
              transition: 'all 0.3s',
              marginBottom: 12
            }}
          >
            <InboxOutlined style={{ fontSize: 24, color: editDragOver ? '#1890ff' : '#999' }} />
            <div style={{ marginTop: 4, fontSize: 13, color: '#666' }}>
              拖拽文件到此处，或<span style={{ color: '#1890ff' }}>点击选择</span>
            </div>
            <div style={{ marginTop: 2, fontSize: 11, color: '#999' }}>
              支持多文件，最多10个
            </div>
            <input
              ref={editFileInputRef}
              type="file"
              multiple
              onChange={(e) => {
                if (e.target.files) handleEditFilesSelect(e.target.files);
                e.target.value = '';
              }}
              style={{ display: 'none' }}
            />
          </div>

          {/* 待上传文件列表 */}
          {editPendingFiles.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>待上传文件：</div>
              {editPendingFiles.map((file, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: 4,
                  marginBottom: 4
                }}>
                  <FileOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                  <div style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </div>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveEditPendingFile(idx)}
                    style={{ fontSize: 12 }}
                  />
                </div>
              ))}
              <Button
                type="primary"
                size="small"
                onClick={handleUploadEditAttachments}
                style={{ marginTop: 8, width: '100%' }}
              >
                上传 {editPendingFiles.length} 个文件
              </Button>
            </div>
          )}

          {/* 上传进度 */}
          {editUploadProgress && (
            <div style={{
              padding: '8px 12px',
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: 6,
              fontSize: 12
            }}>
              <div style={{ marginBottom: 4 }}>
                正在上传 ({editUploadProgress.current}/{editUploadProgress.total})
              </div>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#666' }}>
                {editUploadProgress.fileName}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* 批注弹窗 */}
      {annotationLogId && (
        <AnnotationPanel
          logId={annotationLogId}
          token={localStorage.getItem('notes_token')}
          currentUser={currentUser}
          onClose={() => setAnnotationLogId(null)}
        />
      )}
    </div>
  );
}
